import Chart from "react-apexcharts";
import { ApexOptions } from "apexcharts";
import { useTranslation } from "react-i18next";
import moment from "moment";

const MONTH_IN_MILISECONDS = 2629800000;

interface AreaChartDataProps {
  name?: string;
  data: any;
}

interface Point {
  text: string;
  x: any;
  y: any;
  showAsPointer?: boolean;
}

interface AreaChartProps {
  titulo?: string;
  series: AreaChartDataProps[];
  categories: any;
  color?: string[];
  textColor?: string;
  colorTitle?: string;
  xAxisFormatter?: (val: number) => string;
  tickAmount?: number;
  min?: number;
  max?: number;
  tooltip?: any;
  onClick?: any;
  height?: number;
  width?: number;
  animation?: boolean;
  showAnnotationMonths?: boolean;
  xaxisType?: "category" | "datetime" | "numeric";
  xaxisLabelFormat?: string;
  tooltipTitleFormat?: boolean;
  showXAxisLabel?: boolean;
  showYAxisLabel?: boolean;
  showTooltip?: boolean;
  showVerticalGridLines?: boolean;
  markerStrokeWidth?: number;
  strokeWidth?: number;
  markerSize?: number;
  curveType?:
    | "smooth"
    | "straight"
    | "stepline"
    | ("smooth" | "straight" | "stepline")[];
  xLabelFormatter?: (val: number) => string;
}

const AreaChart: React.FC<AreaChartProps> = (props) => {
  const {
    titulo,
    colorTitle,
    color,
    textColor,
    series,
    categories,
    tickAmount,
    min,
    max,
    xAxisFormatter,
    tooltip,
    onClick,
    height = "auto",
    animation = true,
    showAnnotationMonths,
    xaxisType,
    xaxisLabelFormat,
    tooltipTitleFormat,
    width,
    showXAxisLabel = true,
    showYAxisLabel = true,
    showTooltip = true,
    markerStrokeWidth = 1,
    showVerticalGridLines = false,
    strokeWidth = 4,
    markerSize = 8,
    curveType = "smooth",
    xLabelFormatter,
  } = props;
  const { t } = useTranslation("radar");
  const currentTime = new Date().getTime();

  const getXAxis = () => {
    const xAxis = [];

    const oldestMonth = categories
      ? moment(categories[0]).diff(moment(), "months")
      : 0;

    if (oldestMonth < -3) {
      xAxis.push({
        x: currentTime - MONTH_IN_MILISECONDS * 3,
        strokeDashArray: 0,
        borderColor: "#6d234a",
        label: {
          borderColor: "#6d234a",
          orientation: "horizontal",
          textAnchor: "right",
          style: {
            color: "#fff",
            background: "#6d234a",
          },
          text: t("pages.ArtistProfile.threeMonths"),
        },
      });
    }
    if (oldestMonth < -6) {
      xAxis.push({
        x: currentTime - MONTH_IN_MILISECONDS * 6,
        strokeDashArray: 0,
        borderColor: "#1890ff",
        label: {
          borderColor: "#1890ff",
          orientation: "horizontal",
          textAnchor: "right",
          style: {
            color: "#fff",
            background: "#1890ff",
          },
          text: t("pages.ArtistProfile.sixMonths"),
        },
      });
    }

    if (oldestMonth < -18) {
      xAxis.push({
        x: currentTime - MONTH_IN_MILISECONDS * 18,
        strokeDashArray: 0,
        borderColor: "#37B1E4",
        label: {
          borderColor: "#37B1E4",
          orientation: "horizontal",
          textAnchor: "right",
          style: {
            color: "#fff",
            background: "#37B1E4",
          },
          text: t("pages.ArtistProfile.eighteenMonths"),
        },
      });
    }

    return xAxis;
  };

  const annotations: ApexAnnotations = tooltip
    ? {
        points: tooltip?.points
          ?.filter((point: Point) => point.y)
          .map((point: Point) => ({
            x: point.x,
            y: point.y,
            marker: {
              size: markerSize,
              fillColor: color && color.length > 0 ? color[0] : "#fff",
              radius: 2,
              strokeWidth: markerStrokeWidth,
            },
            label: point.showAsPointer ? { text: point.text } : {},
          })),
        xaxis: showAnnotationMonths ? getXAxis() : [],
      }
    : {
        xaxis: showAnnotationMonths ? getXAxis() : [],
      };

  const options: ApexOptions = {
    chart: {
      type: "area",
      zoom: {
        enabled: false,
      },
      toolbar: {
        show: false,
      },
      events: {
        click: onClick,
      },
      animations: {
        enabled: animation,
      },
    },
    dataLabels: {
      enabled: false,
    },
    fill: {
      type: "solid",
      opacity: 0.2,
    },
    stroke: {
      curve: curveType,
      width: strokeWidth,
    },
    title: {
      text: titulo,
      style: { color: colorTitle ?? "white", fontFamily: "Cabin" },
      align: "left",
    },
    grid: {
      borderColor: "#313131",
      yaxis: {
        lines: {
          show: true,
        },
      },
      xaxis: {
        lines: {
          show: showVerticalGridLines,
        },
      },
      row: {
        opacity: 0.5,
      },
    },
    colors: color,
    yaxis: {
      labels: {
        style: {
          colors: textColor ?? "white",
        },
        formatter: xAxisFormatter,
        show: showYAxisLabel,
      },
      tickAmount: tickAmount,
      max: max,
      min: min,
    },
    xaxis: {
      type: xaxisType,
      tickAmount: 10,
      labels: {
        format: xaxisLabelFormat,
        show: showXAxisLabel,
        style: {
          colors: textColor ?? "white",
          fontSize: "12px",
        },
      },
      categories: categories,
    },
    annotations: annotations,
    legend: {
      fontSize: "16px",
      labels: {
        colors: textColor ?? "white",
      },
    },
    tooltip: (tooltip?.points?.length > 0 && {
      enabled: showTooltip,
      custom: ({ series, seriesIndex, dataPointIndex, w }: any) => {
        const filteredPoints = tooltip.points.filter(
          (point: any) => point.x === categories[dataPointIndex]
        );
        const val = series[seriesIndex][dataPointIndex];
        const title = tooltipTitleFormat
          ? moment(categories[dataPointIndex]).format("DD/MM/YYYY")
          : categories[dataPointIndex];
        const color = w.config.colors ? w.config.colors[seriesIndex] : "#000";
        const serieName = w.config.series[seriesIndex].name;
        return `
            <div class="apexcharts-tooltip-title" style="font-size:12px">${title}</div>
            <div class="apexcharts-tooltip-series-group apexcharts-active" style="order: 1; display: flex;">
              <span class="apexcharts-tooltip-marker" style="background-color: ${color}"></span>
              <div class="apexcharts-tooltip-text" style="font-size:12px">
              <div class="apexcharts-tooltip-y-group">
                <span class="apexcharts-tooltip-text-y-label">${serieName}: </span>
                <span class="apexcharts-tooltip-text-y-value">${
                  val === null
                    ? t("pages.ArtistProfile.noData")
                    : xAxisFormatter
                    ? xAxisFormatter(val)
                    : val
                }</span>
              </div>
                ${
                  (filteredPoints?.length > 0 &&
                    `<div class="apexcharts-tooltip-goals-group">
                      <span class="apexcharts-tooltip-text-goals-label">${
                        tooltip.label
                      }</span>
                    ${filteredPoints.map(
                      (point: any) =>
                        `<span class="apexcharts-tooltip-text-goals-value">${point.text}</span>`
                    )}              
                  </div>`) ||
                  ""
                }                       
            </div>
          </div>
        `;
      },
    }) || {
      enabled: showTooltip,
      x: {
        formatter: xLabelFormatter,
      },
    },
  };
  return (
    <div>
      <Chart
        series={series}
        options={options}
        height={height}
        width={width}
        type="area"
      />
    </div>
  );
};

export default AreaChart;
